import { useClubContext } from "@/hooks/useClubContext";
import { Navigate, useLocation } from "react-router-dom";
import { ReactNode } from "react";

const PLATFORM_HOSTNAMES = new Set([
  "uptheclub.com",
  "www.uptheclub.com",
  "uptheclub.co.uk",
  "www.uptheclub.co.uk",
  "localhost",
  "127.0.0.1",
]);

const isPlatformHost = () => {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return (
    PLATFORM_HOSTNAMES.has(h) ||
    h.endsWith(".lovable.app") ||
    h.endsWith(".lovableproject.com") ||
    h.endsWith(".webcontainer.io")
  );
};

/**
 * When the app is accessed via a custom club domain, the ClubProvider resolves
 * the club from the hostname. This component silently redirects to /club/:slug/*
 * so the existing PublicLayout route structure works seamlessly.
 *
 * Hard guard: on platform hostnames (uptheclub.com, *.lovable.app, etc.) this
 * component is a strict no-op so the marketing site can never be hidden behind
 * a "Site not found" fallback.
 */
const CustomDomainRedirect = ({ children }: { children: ReactNode }) => {
  const { club, loading, isCustomDomain } = useClubContext();
  const location = useLocation();

  if (isPlatformHost()) return <>{children}</>;

  if (!isCustomDomain) return <>{children}</>;

  // Already redirected to /club/:slug path
  if (location.pathname.startsWith("/club/")) return <>{children}</>;

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  }

  if (club) {
    const targetPath = `/club/${club.slug}${location.pathname === "/" ? "" : location.pathname}`;
    return <Navigate to={targetPath + location.search + location.hash} replace />;
  }

  // No club matched this domain — also self-heal any stale service worker / cache
  // so a returning visitor with a poisoned PWA cache recovers on next reload.
  if (typeof window !== "undefined" && "serviceWorker" in navigator) {
    navigator.serviceWorker.getRegistrations?.().then((regs) => {
      regs.forEach((r) => r.unregister().catch(() => {}));
    }).catch(() => {});
    if (typeof caches !== "undefined") {
      caches.keys().then((keys) => keys.forEach((k) => caches.delete(k).catch(() => {}))).catch(() => {});
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <p className="text-xl font-semibold">Site not found</p>
      <p className="text-muted-foreground">This domain hasn't been configured yet.</p>
    </div>
  );
};

export default CustomDomainRedirect;
